/**
 * PlaceholderRenderer — renders placeholder UI for blocks/elements not yet loaded.
 *
 * Variants:
 *   - block-generating: Block being generated by LLM. Shows role-based skeleton layout.
 *   - data-loading:     Datasource not connected yet. Shows skeleton table/list.
 *   - image-pending:    Image being generated/uploaded. Shows image icon + aspect ratio.
 *   - variable-unbound: Variable not bound. Shows inline warning badge.
 *   - content-empty:    Empty content slot. Shows dashed outline.
 *
 * Props via standard UiElement fields (no type extension needed):
 *   - variant: placeholder kind
 *   - text:    label (block name, variable name, etc.)
 *   - className: contains "role-{nav|hero|section|footer}" hint for height
 */

import type { RendererProps } from "../core/element-registry"
import { Skeleton } from "../components/ui/skeleton"
import { cn } from "../lib/utils"

// ─── Role → Height / Skeleton Pattern ──────────────────────────

const ROLE_HEIGHT: Record<string, string> = {
  nav: "h-16",
  hero: "h-[400px]",
  section: "h-[300px]",
  content: "h-[250px]",
  sidebar: "h-full min-h-[400px]",
  footer: "h-[200px]",
}

function extractRole(className?: string): string {
  if (!className) return "section"
  const match = className.match(/role-(\w+)/)
  return match?.[1] ?? "section"
}

// ─── Main Renderer ─────────────────────────────────────────────

export function PlaceholderRenderer({ element }: RendererProps) {
  const variant = element.variant ?? "block-generating"
  const role = extractRole(element.className)
  const label = element.text ?? "Block"
  const rawSize = element.size
  const sizeClass = rawSize === "sm"
    ? "max-w-sm"
    : rawSize === "lg"
      ? "min-h-96 w-full"
      : rawSize === "full"
        ? "min-h-full w-full"
        : "w-full"
  const numericSize = typeof rawSize === "string" && /^\d+(?:\.\d+)?$/.test(rawSize)
    ? Number(rawSize)
    : undefined

  let content: React.ReactNode
  switch (variant) {
    case "block-generating":
      content = <BlockGeneratingPlaceholder role={role} label={label} />
      break
    case "data-loading":
      content = <DataLoadingPlaceholder label={label} />
      break
    case "image-pending":
      content = <ImagePendingPlaceholder />
      break
    case "variable-unbound":
      content = <VariableUnboundPlaceholder name={label} />
      break
    case "content-empty":
      content = <ContentEmptyPlaceholder label={label} />
      break
    default:
      content = <BlockGeneratingPlaceholder role="section" label={label} />
  }
  return <div className={cn(sizeClass, element.className)} style={numericSize ? { width: numericSize, minHeight: numericSize } : undefined}>{content}</div>
}

// ─── Block Generating ──────────────────────────────────────────

function BlockGeneratingPlaceholder({ role, label }: { role: string; label: string }) {
  const height = ROLE_HEIGHT[role] ?? "h-[250px]"

  return (
    <div className={cn("w-full", height, "border-2 border-dashed border-primary/20 rounded-lg flex flex-col items-center justify-center gap-3 animate-pulse bg-muted/10")}>
      <SkeletonPattern role={role} />
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">
          {label}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary/70">
          generating
        </span>
      </div>
      <BouncingDots />
    </div>
  )
}

function SkeletonPattern({ role }: { role: string }) {
  switch (role) {
    case "nav":
      return (
        <div className="flex items-center gap-3 px-4 w-full">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-4 w-24" />
          <div className="flex-1" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      )
    case "hero":
      return (
        <div className="flex flex-col items-center gap-4 px-8">
          <Skeleton className="h-8 w-3/4 max-w-md" />
          <Skeleton className="h-4 w-1/2 max-w-sm" />
          <div className="flex gap-3 mt-2">
            <Skeleton className="h-10 w-32 rounded-lg" />
            <Skeleton className="h-10 w-32 rounded-lg" />
          </div>
        </div>
      )
    case "footer":
      return (
        <div className="flex gap-8 px-8 w-full max-w-4xl">
          <div className="flex flex-col gap-2 flex-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      )
    case "sidebar":
      return (
        <div className="flex flex-col gap-3 px-4 w-full">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      )
    default:
      // section / content — card grid
      return (
        <div className="flex gap-4 px-8">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex flex-col gap-2 w-40">
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      )
  }
}

function BouncingDots() {
  return (
    <div className="flex gap-1">
      <div className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: "0ms" }} />
      <div className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: "150ms" }} />
      <div className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  )
}

// ─── Data Loading ──────────────────────────────────────────────

function DataLoadingPlaceholder({ label }: { label: string }) {
  return (
    <div className="w-full p-4 border border-dashed border-border rounded-lg space-y-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 rounded" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
        </div>
      ))}
    </div>
  )
}

// ─── Image Pending ─────────────────────────────────────────────

function ImagePendingPlaceholder() {
  return (
    <div className="w-full aspect-video bg-muted/20 border border-dashed border-border rounded-lg flex items-center justify-center">
      <svg className="w-8 h-8 text-muted-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
      </svg>
    </div>
  )
}

// ─── Variable Unbound ──────────────────────────────────────────

function VariableUnboundPlaceholder({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-700">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
      </svg>
      {name}
    </span>
  )
}

// ─── Content Empty ─────────────────────────────────────────────

function ContentEmptyPlaceholder({ label }: { label: string }) {
  return (
    <div className="w-full h-32 border-2 border-dashed border-border/50 rounded-lg flex items-center justify-center">
      <span className="text-xs text-muted-foreground/40">{label} — no content</span>
    </div>
  )
}
